import {
  typedCollection,
  typedDoc,
  getCollection,
  getDocument,
  updateDocument,
  deleteDocument,
  orderBy,
  where,
  serverTimestamp,
} from '@/lib/firebase/firestore'
import { approveProviderInvoiceFn } from '@/lib/firebase/functions'
import type {
  ProviderInvoiceDocument,
  ProviderInvoiceLineItem,
  ProviderInvoiceStatus,
  MemberTaxProfileDocument,
} from '@/types'

const col = () => typedCollection<ProviderInvoiceDocument>('providerInvoices')

export const adminInvoicesService = {
  async listAll() {
    return getCollection(col(), orderBy('submittedAt', 'desc'))
  },

  async listByStatus(status: ProviderInvoiceStatus) {
    return getCollection(col(), where('status', '==', status), orderBy('submittedAt', 'desc'))
  },

  async approve(id: string): Promise<void> {
    await approveProviderInvoiceFn({ invoiceId: id })
  },

  async reject(id: string, note: string): Promise<void> {
    const ref = typedDoc<ProviderInvoiceDocument>('providerInvoices', id)
    await updateDocument(ref, {
      status: 'rejected',
      rejectionNote: note,
      updatedAt: serverTimestamp(),
    } as unknown as Partial<ProviderInvoiceDocument>)
  },

  async markPaid(id: string): Promise<void> {
    const ref = typedDoc<ProviderInvoiceDocument>('providerInvoices', id)
    await updateDocument(ref, {
      status: 'paid',
      paidAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as unknown as Partial<ProviderInvoiceDocument>)
  },

  async editLineItems(id: string, lineItems: ProviderInvoiceLineItem[]): Promise<void> {
    const subtotal = lineItems.reduce((acc, li) => acc + (Number(li.amount) || 0), 0)
    const ref = typedDoc<ProviderInvoiceDocument>('providerInvoices', id)
    await updateDocument(ref, {
      lineItems,
      subtotal,
      updatedAt: serverTimestamp(),
    } as unknown as Partial<ProviderInvoiceDocument>)
  },

  async deleteInvoice(id: string): Promise<void> {
    await deleteDocument(typedDoc<ProviderInvoiceDocument>('providerInvoices', id))
  },

  async getOne(id: string): Promise<(ProviderInvoiceDocument & { id: string }) | null> {
    const ref = typedDoc<ProviderInvoiceDocument>('providerInvoices', id)
    const data = await getDocument(ref)
    return data ? { ...data, id } : null
  },

  async getMemberTaxProfile(memberId: string): Promise<MemberTaxProfileDocument | null> {
    const ref = typedDoc<MemberTaxProfileDocument>('memberTaxProfiles', memberId)
    return getDocument(ref)
  },
}
